import { useState } from 'react';
import {
  X,
  Server,
  Network,
  Clock,
  HardDrive,
  ShieldCheck,
  FileSpreadsheet,
  DollarSign,
  CheckCircle2,
  AlertTriangle,
  Layers,
  Target,
} from 'lucide-react';

interface PlanDirecteurTechniqueModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type TabId = 'esprit' | 'sources' | 'structure' | 'qualite';

const tabs: Array<{ id: TabId; label: string; icon: any }> = [
  { id: 'esprit', label: 'Esprit', icon: Target },
  { id: 'sources', label: 'Sources', icon: FileSpreadsheet },
  { id: 'structure', label: 'Structure', icon: Layers },
  { id: 'qualite', label: 'Qualité', icon: CheckCircle2 },
];

const sourceBlocks = [
  {
    icon: Server,
    title: 'Infrastructure & équipements',
    text: 'Serveurs, stockage, réseau, WiFi, postes, UPS, garanties, fin de vie et architecture actuelle.',
  },
  {
    icon: Clock,
    title: 'RTO / RPO',
    text: 'Objectifs de restauration par service critique, pertes de données acceptables et temps de reprise.',
  },
  {
    icon: HardDrive,
    title: 'Sauvegardes & hébergement',
    text: 'Fréquences, emplacements, outils, hébergement physique, cloud, relève et archivage.',
  },
  {
    icon: ShieldCheck,
    title: 'Sécurité & conformité',
    text: 'EDR, antispam, MFA, Intune, Loi 25, pratiques de protection et éléments de conformité.',
  },
  {
    icon: DollarSign,
    title: 'Budget & investissements',
    text: 'Matériel, licences, services, remplacements, scénarios budgétaires et priorités d’investissement.',
  },
  {
    icon: Network,
    title: 'Télécom, programmation et autres',
    text: 'Téléphonie, outils spécifiques, automatisations, systèmes connexes et dépendances techniques.',
  },
];

const expectedSections = [
  'Objectif du plan directeur technique',
  'Compréhension du client et contraintes d’affaires',
  'Description de l’environnement et architecture réseau',
  'Analyse de l’équipement informatique et logiciel',
  'RTO/RPO par service critique',
  'Stratégies de sauvegardes et hébergement',
  'Sécurité, conformité Loi 25 et risques techniques',
  'Budget détaillé et scénarios d’investissement',
  'Recommandations techniques et feuille de route',
];

const qualityRules = [
  'Utiliser les données exactes du questionnaire technique; ne pas produire un document générique.',
  'Transformer les listes copiées d’Excel en tableaux lisibles: serveurs, stockage, UPS, réseau, postes, licences et budget.',
  'Nommer clairement les informations manquantes au lieu de les inventer.',
  'Relier chaque recommandation à un risque, une contrainte ou un objectif technique observable.',
  'Distinguer les constats techniques, les recommandations, les hypothèses et les décisions à valider.',
  'Adapter la profondeur au niveau de qualité choisi: court, enrichi, complet, géré ou exécutif.',
];

export default function PlanDirecteurTechniqueModal({ isOpen, onClose }: PlanDirecteurTechniqueModalProps) {
  const [activeTab, setActiveTab] = useState<TabId>('esprit');

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
      <div className="w-[92vw] h-[90vh] max-w-[1500px] rounded-2xl bg-white shadow-2xl flex flex-col overflow-hidden">
        <div className="bg-gradient-to-r from-slate-800 via-blue-700 to-cyan-700 text-white px-8 py-6 flex items-center justify-between">
          <div className="flex items-center gap-4 min-w-0">
            <div className="w-14 h-14 rounded-xl bg-white/15 flex items-center justify-center shadow-lg">
              <Server className="w-7 h-7" />
            </div>
            <div className="min-w-0">
              <h2 className="text-2xl font-bold mb-1">Plan Directeur TI - Analyse Technique & Budget</h2>
              <p className="text-sm text-white/90">
                Espace du document • Infrastructure, RTO/RPO, sauvegardes, sécurité, logiciels et investissements
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-white/20 transition-colors">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="flex gap-2 border-b-2 border-gray-200 px-8 pt-4 pb-3 bg-gradient-to-b from-gray-50 to-white overflow-x-auto">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold transition-all whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'bg-blue-700 text-white shadow-lg'
                    : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </div>

        <div className="flex-1 overflow-y-auto bg-gray-50 px-8 py-6 text-sm text-gray-700">
          {activeTab === 'esprit' && (
            <div className="space-y-5">
              <div className="bg-white rounded-xl border border-blue-200 p-5 shadow-sm">
                <h3 className="text-lg font-bold text-gray-900 mb-2">Analyser l’environnement réel avant de recommander</h3>
                <p className="leading-relaxed">
                  Ce document sert à produire une lecture technique et budgétaire complète de l’environnement TI. Il ne
                  remplace pas le Plan Directeur stratégique 3-5 ans: il le complète avec les preuves techniques, les
                  contraintes d’infrastructure, les risques opérationnels, les coûts et les scénarios de modernisation.
                </p>
              </div>

              <div className="grid md:grid-cols-3 gap-4">
                <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
                  <h4 className="text-sm font-bold text-gray-900 mb-2">À qui on le livre</h4>
                  <p className="text-xs leading-relaxed">
                    Direction client, responsables TI, comité d’investissement, Directeur TI BZ et spécialistes qui
                    devront chiffrer, planifier ou livrer les changements.
                  </p>
                </div>
                <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
                  <h4 className="text-sm font-bold text-gray-900 mb-2">Pourquoi on le produit</h4>
                  <p className="text-xs leading-relaxed">
                    Pour passer d’une perception générale à un portrait technique défendable: risques, fin de vie,
                    continuité, sécurité, sauvegardes, budget et ordre logique des investissements.
                  </p>
                </div>
                <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
                  <h4 className="text-sm font-bold text-gray-900 mb-2">Sortie attendue</h4>
                  <p className="text-xs leading-relaxed">
                    Un document technique structuré, lisible par la direction, avec tableaux d’inventaire, RTO/RPO,
                    architecture, recommandations, risques et budget.
                  </p>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'sources' && (
            <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
              {sourceBlocks.map((block) => {
                const Icon = block.icon;
                return (
                  <div key={block.title} className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 rounded-lg bg-blue-50 text-blue-700 flex items-center justify-center">
                        <Icon className="w-5 h-5" />
                      </div>
                      <div>
                        <h4 className="text-sm font-bold text-gray-900 mb-1">{block.title}</h4>
                        <p className="text-xs leading-relaxed text-gray-600">{block.text}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {activeTab === 'structure' && (
            <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Structure attendue du livrable</h3>
              <div className="grid md:grid-cols-2 gap-3">
                {expectedSections.map((section, index) => (
                  <div key={section} className="flex items-start gap-3 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
                    <span className="shrink-0 w-7 h-7 rounded-full bg-blue-700 text-white text-xs font-bold flex items-center justify-center">
                      {index + 1}
                    </span>
                    <p className="text-sm font-semibold text-gray-800 pt-1">{section}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'qualite' && (
            <div className="space-y-5">
              <div className="bg-amber-50 rounded-xl border border-amber-200 p-5 shadow-sm flex items-start gap-3">
                <AlertTriangle className="w-6 h-6 text-amber-700 shrink-0 mt-0.5" />
                <div>
                  <h3 className="text-lg font-bold text-amber-950 mb-1">Point de vigilance</h3>
                  <p className="text-sm text-amber-900">
                    Le document doit rester technique et vérifiable. Quand une donnée n’est pas fournie, PRIsME peut
                    proposer une hypothèse ou une valeur recommandée, mais elle doit être identifiable comme hypothèse.
                  </p>
                </div>
              </div>
              <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
                <h4 className="text-sm font-bold text-gray-900 mb-3">Définition de “document prêt”</h4>
                <div className="grid md:grid-cols-2 gap-3">
                  {qualityRules.map((rule) => (
                    <div key={rule} className="flex items-start gap-2 rounded-lg border border-emerald-100 bg-emerald-50 px-3 py-2">
                      <CheckCircle2 className="w-4 h-4 text-emerald-700 shrink-0 mt-0.5" />
                      <p className="text-xs leading-relaxed text-gray-700">{rule}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
